// Animal fever's hand-drawn heads (art/aseprite/src/animal_heads.lua -> client/art/aseprite/animal-heads/): one idle loop per
// animal, read here into frames the glass mask can stamp. The loop runs on the clock and the visitor's own phase, never on who
// is knocking. Sheet palette: 1 ink = the figure (shade 0), 2 blue = detail (1), 3 paper = pale (2), 4 glow = lit (3).
import { decodeIndexed } from './eyes.js';

export const FPS = 15;
const SHADE_OF = { 1: 0, 2: 1, 3: 2, 4: 3 };
// each head's centre in its frame (sheet px = mask px): it goes where a person's head centre would be
export const ANCHORS = { pigeon: [64, 58], cowboy: [64, 73], afrocat: [64, 80], hamster: [64, 73],
  dog: [64, 66], frog: [64, 74], goat: [64, 74], capy: [64, 78], owl: [64, 76], raccoon: [64, 64],
  whale: [64, 76], laser: [64, 68], influencer: [64, 70], anon: [64, 64], // the degen visitors (game/degens.js)
  frens: [64, 76], balloon: [64, 84], ice: [64, 64], farm: [64, 72], rocket: [64, 64],
  // the drawn neighbours (game/regulars.js) and the two still in draft (game/drafts.js)
  'n-eyes': [64, 70], 'n-moth': [64, 72], 'n-set': [64, 72], 'n-spider': [64, 72], goblin: [64, 74], 'brain-cap': [64, 72], 'brain-horns': [64, 72],
  copfrog: [64, 72], mole: [64, 72], crab: [64, 62], bouffant: [64, 78], hood: [64, 78], rasta: [64, 72], skull: [64, 66] };

const BASE = new URL('./art/aseprite/animal-heads/', import.meta.url);
const isNode = !!globalThis.process?.versions?.node;
async function bytes(url) {
  if (isNode) { const fs = 'node:fs/promises'; return new Uint8Array(await (await import(fs)).readFile(url)); }
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
}

// the sheet -> per animal: its frames (w, h, the pixels as [x, y, shade] runs) and its timeline, one frame index per game frame
export function parseHeads(png, sheet) {
  const frames = sheet.frames.map(({ frame: f }) => {
    const px = [];
    for (let y = 0; y < f.h; y++) for (let x = 0; x < f.w; x++) {
      const s = SHADE_OF[png.index[(f.y + y) * png.w + f.x + x]];
      if (s !== undefined) px.push(x, y, s);
    }
    return { w: f.w, h: f.h, px: Int16Array.from(px) };
  });
  const out = {};
  for (const tag of sheet.meta.frameTags) {
    const timeline = [];
    for (let i = tag.from; i <= tag.to; i++) for (let k = Math.max(1, Math.round(sheet.frames[i].duration * FPS / 1000)); k > 0; k--) timeline.push(i - tag.from);
    out[tag.name] = { frames: frames.slice(tag.from, tag.to + 1), timeline, anchor: ANCHORS[tag.name] };
  }
  return out;
}

let heads = null;
try {
  const [png, sheet] = await Promise.all([bytes(new URL('animal-heads-sheet.png', BASE)).then(decodeIndexed),
    bytes(new URL('animal-heads-sheet.json', BASE)).then(b => JSON.parse(new TextDecoder().decode(b)))]);
  heads = parseHeads(png, sheet);
} catch (e) { console.warn('animal heads:', e.message); }

export const drawnAnimal = kind => !!heads?.[kind];
// which frame of the loop shows t seconds in, shifted by the visitor's own phase so two visitors never move in step
export function headFrame(kind, t = 0, phase = 0) {
  const h = heads?.[kind];
  if (!h) return -1;
  const n = h.timeline.length;
  return h.timeline[(((Math.floor(t * FPS) + phase) % n) + n) % n];
}
export const headArt = (kind, i) => (heads?.[kind] ? { ...heads[kind].frames[i], anchor: heads[kind].anchor } : null);
